// ---------------------------------------------------------------------------
// encounterService
//
// An Encounter is a logical container that groups one or more `Conversation`
// documents belonging to the same clinical patient visit. The motivating use
// case is the cross-provider workflow: one provider records a session on
// their own account, then another provider records a continuation on theirs,
// and the resulting note is one HPI stitched from both transcripts.
//
// Schema (Firestore collection `Encounter/{encounterId}`):
//
//   patient_id            local Patient id (null if EHR-only patient)
//   ehr_patient_id        EHR patient id (for EHR-only flows / dual lookup)
//   patient_name          denormalized for list rendering
//   company_id            access scope (cross-provider read inside one company)
//   facility_id           denormalized
//   contributor_user_ids  denormalized union of every Conversation.user_id;
//                         the creating provider is always included so the
//                         Firestore create rule can authorize the write
//   conversation_ids      ordered (chronological) list of member Conversations
//   workflow_type         'medical_scribing' for v1 (live translation stays standalone)
//   template_type         preferred note template id
//   specialty             carried from first conversation
//   status                'open' | 'closed'
//   start_time            earliest member conversation start_time
//   end_time              latest member conversation end_time (null while open)
//   total_minutes         sum of member minutes
//   summary               most recent member summary (or override)
//   created_at, updated_at
//   is_removed            soft-delete flag mirroring Conversation
//
// Backwards compatibility:
//
//   A `Conversation` document without an `encounter_id` field is treated as
//   an *implicit single-conversation encounter*. The grouping helpers in
//   `encounterUtils.js` synthesize an EncounterView for those without ever
//   writing to Firestore. The UI renders implicit encounters identically to
//   real ones, so no migration is required to ship this feature.
//
// Pure helpers (grouping, aggregation, contributor extraction) live in
// `encounterUtils.js` and are re-exported here for ergonomic import.
// ---------------------------------------------------------------------------

import {analytics} from './analytics';
import {logger, LogFlow, describeError} from './logger';
import {patientService} from './patientService';
import {
  dlSoftDeleteEncounter,
  dlSoftDeleteSession,
  dlSetEncounterPatient,
  dlSetEncounterSummary,
  dlSetEncounterLanguage,
  dlAdoptSessionIntoEncounter,
  dlDetachSessionFromEncounter,
  dlCloseEncounter,
  isDataLayerNotFound,
  fetchRecentSessions,
  fetchRemovedSessions,
  fetchPatientEncounters,
  fetchCompanyEncounters,
  fetchEncounterDetail,
  encounterSessionSummaryToLegacyConversation,
  mintedEncounterIdFor,
  recentSessionsToEncounterViews,
  recentSessionRowToLegacyConversation,
  patientEncounterRowToLegacyEncounterDoc,
  companyEncounterRowToLegacyEncounterDoc,
  tagsToLegacyFields,
} from './dataLayerClient';

import {
  ENCOUNTER_STATUS,
  OPEN_ENCOUNTER_LOOKBACK_MS,
  SEGMENT_MARKER_CONTENT_TYPE,
  SEGMENT_MARKER_SPEAKER,
  SEGMENT_ACCENT_PALETTE,
  pickSegmentAccent,
  toJsDate,
  implicitEncounterId,
  isImplicitEncounterId,
  extractContributors,
  inferSegmentRole,
  buildSegmentMarkerMessage,
  aggregateEncounterMessages,
  buildEncounterViewFromDoc,
  buildImplicitEncounterView,
  groupConversationsIntoEncounters,
  pickContinuableEncounter,
  pickRepresentativeConversation,
  isEncounterContinuable,
  isEncounterRecording,
  isEncounterPrivate,
  isEncounterVisibleToUser,
  filterEncountersForViewer,
  isTodaysEncounter,
  pickTodaysEncounter,
  isScribeWorkflow,
  hasZoomMeeting,
  isTelehealthEncounter,
  isPhoneEncounter,
  resolveEncounterModality,
  getPatientIdentity,
  isCrossPatient,
  filterAggregatableSiblings,
} from './encounterUtils';

// Best-effort PostHog capture (no-ops cleanly in tests / before init). Used to
// surface when the encounter-aggregation safety guards have to drop a sibling
// or reject a cross-patient encounter bind, so we can spot bad linkages in the
// field instead of silently producing a contaminated note.
function captureGuardEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
) {
  try {
    analytics.capture(eventName, properties);
  } catch {
    /* ignore telemetry failures */
  }
}

// Scoped logger for encounter lifecycle. Logs ids, workflow/template type,
// counts, and durations — never patient names or summaries (PHI).
const log = logger.scope(LogFlow.Encounter);

// Re-export everything from the pure helpers so callers only need to import
// from `encounterService`. Tests can import directly from `encounterUtils`
// to skip the firebase module graph.
export {
  ENCOUNTER_STATUS,
  OPEN_ENCOUNTER_LOOKBACK_MS,
  SEGMENT_MARKER_CONTENT_TYPE,
  SEGMENT_MARKER_SPEAKER,
  SEGMENT_ACCENT_PALETTE,
  pickSegmentAccent,
  toJsDate,
  implicitEncounterId,
  isImplicitEncounterId,
  extractContributors,
  inferSegmentRole,
  buildSegmentMarkerMessage,
  aggregateEncounterMessages,
  buildEncounterViewFromDoc,
  buildImplicitEncounterView,
  groupConversationsIntoEncounters,
  pickContinuableEncounter,
  pickRepresentativeConversation,
  isEncounterContinuable,
  isEncounterRecording,
  isEncounterPrivate,
  isEncounterVisibleToUser,
  filterEncountersForViewer,
  isTodaysEncounter,
  pickTodaysEncounter,
  isScribeWorkflow,
  hasZoomMeeting,
  isTelehealthEncounter,
  isPhoneEncounter,
  resolveEncounterModality,
  getPatientIdentity,
  isCrossPatient,
  filterAggregatableSiblings,
};

export const ENCOUNTER_COLLECTION = 'Encounter';

// ─── Encounter CRUD (backend REST) ──────────────────────────────────────────
//
// (createEncounter is RETIRED: encounters are only materialized server-side
// now — dlStartSession creates/joins one for every new session, legacy
// sessions carry a minted `enc-{sessionId}` twin, and retroactive grouping
// goes through POST /api/v2/encounters/:id/adopt-session.)

/**
 * Read one encounter, in the legacy Encounter-doc shape callers render.
 *
 * Schema V2: GET /api/v1/encounters/:encounterId (tenancy + session privacy
 * enforced server-side). Returns null on 404 — same contract the Firestore
 * `!snap.exists()` path had. Fields not exposed by the REST detail
 * (`ehr_patient_id`, `facility_id`) are null/'' — callers that need the EHR
 * patient id resolve it from their own patient context.
 */
export async function getEncounter(encounterId: string) {
  if (!encounterId || isImplicitEncounterId(encounterId)) return null;
  let detail;
  try {
    detail = await fetchEncounterDetail(encounterId, {includeSections: false});
  } catch (err) {
    if (isDataLayerNotFound(err)) return null;
    throw err;
  }
  if (!detail?.encounterId) return null;
  const sessions = Array.isArray(detail.sessions) ? detail.sessions : [];
  const anyOpen = sessions.some(
    s => s.status === 'active' || s.status === 'created',
  );
  const startTimes = sessions
    .map(s => toJsDate(s.startedAt))
    .filter(Boolean) as Date[];
  const firstTags = sessions[0]?.tags || [];
  const legacy = tagsToLegacyFields(firstTags);
  return {
    id: detail.encounterId,
    patient_id: detail.patientId ?? null,
    ehr_patient_id: null,
    patient_name: detail.patientName || '',
    company_id: null,
    facility_id: '',
    contributor_user_ids: detail.providerUserIds || [],
    conversation_ids: sessions.map(s => s.sessionId).filter(Boolean),
    workflow_type: legacy.workflowType || 'medical_scribing',
    template_type: legacy.templateType || 'soap',
    specialty: legacy.specialty || '',
    status: anyOpen ? ENCOUNTER_STATUS.OPEN : ENCOUNTER_STATUS.CLOSED,
    start_time: startTimes.length
      ? new Date(Math.min(...startTimes.map(d => d.getTime())))
      : toJsDate(detail.createdAt),
    end_time: anyOpen ? null : toJsDate(detail.updatedAt),
    total_minutes: Number(detail.sessionsTotalMin) || 0,
    summary: detail.summary || '',
    visibility: detail.visibility || null,
    is_removed: false,
    created_at: toJsDate(detail.createdAt),
    updated_at: toJsDate(detail.updatedAt),
    encounterNoteIds: (detail.notes || []).map(n => n.noteId).filter(Boolean),
    _v2: true,
    _v2Sessions: sessions,
  };
}

/**
 * Resolve the encounter id a "Continue" should join for a legacy/implicit
 * (synthetic) encounter view.
 *
 * Schema V2 replaces the old client-side materialization (createEncounter +
 * back-link transaction): every legacy-created session already has a REAL
 * minted 1:1 Encounter doc (`enc-{conversationId}`) maintained server-side,
 * so continuing a synthetic view just means joining that doc — the new
 * session's `encounter_id` binds it and the server projection takes care of
 * membership/aggregates.
 *
 * Returns the minted id when its doc exists, or null when it doesn't (data
 * predating the V2 backfill) — callers then fall back to the legacy
 * materialization so Continue never silently breaks.
 */
export async function resolveMintedEncounterIdForSyntheticView(
  encounterView: any,
): Promise<string | null> {
  const seedId = encounterView?.conversations?.[0]?.id || null;
  if (!seedId) return null;
  const mintedId = mintedEncounterIdFor(seedId);
  try {
    const detail = await fetchEncounterDetail(mintedId, {
      includeSections: false,
    });
    if (detail?.encounterId) return mintedId;
  } catch (err) {
    if (!isDataLayerNotFound(err)) {
      console.warn(
        `[encounterService] could not verify minted encounter ${mintedId}`,
        err?.message,
      );
    }
  }
  return null;
}

// Schema V2: patient identity is written through POST
// /api/v2/encounters/:id/patient, which projects it onto the encounter's V2
// + legacy fields (including the legacy search index) server-side. `summary`
// is accepted for signature stability but no longer needed — the server owns
// the search-token refresh.
export async function reassignEncounterPatient({
  encounterId,
  patientId = null,
  ehrPatientId = null,
  patientName = '',
  summary: _summary = '',
}: Record<string, any> = {}) {
  if (!encounterId || isImplicitEncounterId(encounterId)) return null;
  await dlSetEncounterPatient({
    encounterId,
    patientId: patientId || null,
    ehrPatientId: ehrPatientId || null,
    patientName: patientName || '',
  });
  return {
    id: encounterId,
    patient_id: patientId || null,
    ehr_patient_id: ehrPatientId || null,
    patient_name: patientName || '',
  };
}

// Schema V2: persist the interpretation language for an encounter through
// PATCH /api/v2/encounters/:id/language, which stamps it onto every member
// session (V2 + legacy Conversation mirror) server-side. Used by the Continue
// flow when a closed encounter is resumed with live translation but never
// captured a language. No-op for implicit (unsynced legacy) ids — those have
// no server doc to write to.
export async function setEncounterLanguage({
  encounterId,
  language,
  dialect = null,
}: Record<string, any> = {}) {
  const cleanLanguage = String(language || '').trim();
  if (!encounterId || isImplicitEncounterId(encounterId) || !cleanLanguage) {
    return null;
  }
  await dlSetEncounterLanguage({
    encounterId,
    language: cleanLanguage,
    dialect: dialect ? String(dialect).trim() : null,
  });
  return {
    id: encounterId,
    foreign_language: cleanLanguage,
    dialect: dialect || null,
  };
}

export async function softDeleteEncounter(encounterId: string) {
  if (!encounterId || isImplicitEncounterId(encounterId)) return null;

  // Load the member list BEFORE deleting — the detail read may 404 after.
  const encounter = await getEncounter(encounterId);
  if (!encounter) return null;

  // Schema V2: DELETE /api/v2/encounters/:id flags the V2 doc + legacy
  // mirror and recomputes projections in one server transaction, then each
  // member session is soft-deleted through its own transaction so the
  // per-user lists hide them too.
  await dlSoftDeleteEncounter({encounterId});
  const memberIds = Array.isArray(encounter.conversation_ids)
    ? encounter.conversation_ids.filter(Boolean)
    : [];
  await Promise.all(
    memberIds.map((conversationId: string) =>
      dlSoftDeleteSession({sessionId: conversationId}).catch(err => {
        console.warn(
          `[encounterService] could not soft-delete conversation ${conversationId} for encounter ${encounterId}`,
          err?.message,
        );
      }),
    ),
  );
  return {...encounter, id: encounterId, is_removed: true};
}

/**
 * Link an EXISTING Conversation/session into an Encounter.
 *
 * Schema V2: POST /api/v2/encounters/:id/adopt-session moves the session out
 * of its current encounter (usually its minted 1:1 twin) into the target,
 * updating membership/contributors/aggregates on both sides and stamping the
 * legacy `encounter_id` — one server transaction. (Joining a NEW session is
 * dlStartSession({encounterId}), not this.)
 *
 * The client-side patient-safety backstop survives the transport change: an
 * encounter groups sessions for ONE patient, and linking a different
 * patient's session is what produced the "two patients in one note"
 * incident. We check the target's patient (REST detail) against the
 * conversation's before adopting and refuse on a provable mismatch.
 */
export async function attachConversationToEncounter({
  encounterId,
  conversation,
}: {
  encounterId: string;
  conversation: any;
}) {
  if (!encounterId || isImplicitEncounterId(encounterId)) {
    throw new Error(
      'attachConversationToEncounter: real encounter id required',
    );
  }
  if (!conversation?.id)
    throw new Error('attachConversationToEncounter: conversation.id required');

  const enc = await getEncounter(encounterId);
  if (!enc) throw new Error(`Encounter ${encounterId} not found`);

  if (isCrossPatient(enc, conversation)) {
    const e = getPatientIdentity(enc);
    const c = getPatientIdentity(conversation);
    captureGuardEvent('encounter_attach_patient_mismatch', {
      encounter_id: encounterId,
      conversation_id: conversation.id,
      encounter_patient_id: e.patientId || e.ehrPatientId || null,
      conversation_patient_id: c.patientId || c.ehrPatientId || null,
    });
    log.warn('attach_patient_mismatch_refused', {
      encounter_id: encounterId,
      conversation_id: conversation.id,
      encounter_patient_id: e.patientId || e.ehrPatientId || null,
      conversation_patient_id: c.patientId || c.ehrPatientId || null,
    });
    throw new Error(
      `attachConversationToEncounter: patient mismatch — refusing to link conversation ${conversation.id} ` +
        `(patient ${c.patientId || c.ehrPatientId || '?'}) into encounter ${encounterId} ` +
        `(patient ${e.patientId || e.ehrPatientId || '?'})`,
    );
  }

  await dlAdoptSessionIntoEncounter({
    encounterId,
    sessionId: conversation.id,
  });
}

/**
 * Detach a Conversation from its Encounter — used by "ungroup" / undo flows
 * (ScribePage discards a just-recorded session out of the encounter it
 * joined).
 *
 * Schema V2: POST /api/v2/encounters/:id/detach-session {sessionId} moves
 * the session back to standalone (its minted `enc-{sessionId}` twin) and
 * recomputes aggregates on both sides server-side. Legacy parity: the old
 * client transaction silently no-oped when the session wasn't a member (or
 * the encounter didn't exist) — the server signals that stale state with a
 * 400/404, which we swallow here so callers keep the idempotent contract.
 */
export async function detachConversationFromEncounter({
  encounterId,
  conversationId,
}: {
  encounterId: string;
  conversationId: string;
}) {
  if (!encounterId || isImplicitEncounterId(encounterId)) return;
  if (!conversationId) return;
  try {
    await dlDetachSessionFromEncounter({
      encounterId,
      sessionId: conversationId,
    });
  } catch (err: any) {
    const status = err?.status ?? err?.statusCode;
    if (status === 400 || status === 404) {
      // Not a member / already gone — nothing to detach (stale client state).
      log.info('encounter_detach_noop', {
        encounter_id: encounterId,
        conversation_id: conversationId,
        status,
      });
      return;
    }
    throw err;
  }
}

/**
 * Combine two or more existing standalone Conversations into a single
 * Encounter. Used by the "Combine into one encounter" patient-detail action
 * when the provider wants to retroactively group sessions that weren't
 * captured under a continued-encounter flow.
 *
 * Schema V2: no new Encounter doc is minted client-side. The FIRST
 * (chronologically earliest) session's own encounter — its explicit
 * `encounter_id`, or the minted `enc-{sessionId}` twin every synced session
 * has — becomes the target, and each remaining session is moved into it via
 * POST /api/v2/encounters/:id/adopt-session (aggregates recomputed on both
 * sides server-side). Patient identity and the combined summary are then
 * projected through POST .../patient and PATCH .../summary.
 *
 * `creatorUserId` is kept for signature stability (contributors are now
 * derived server-side from the member sessions).
 */
export async function combineConversationsIntoNewEncounter({
  conversations,
  creatorUserId,
  companyId = null,
  facilityId: _facilityId = '',
  patientId = null,
  ehrPatientId = null,
  patientName = '',
  workflowType: _workflowType = 'medical_scribing',
  templateType: _templateType = 'soap',
  specialty: _specialty = '',
  // Visibility is inherited from the target encounter (stamped at session
  // start from the creator's session-privacy restriction); the parameter is
  // kept for signature stability.
  restrictedVisibility: _restrictedVisibility = false,
}: Record<string, any>) {
  if (!Array.isArray(conversations) || conversations.length < 2) {
    throw new Error(
      'combineConversationsIntoNewEncounter: need at least two conversations',
    );
  }
  if (!creatorUserId) {
    throw new Error(
      'combineConversationsIntoNewEncounter: creatorUserId required',
    );
  }
  const ordered = [...conversations].sort((a, b) => {
    const ta = toJsDate(a.start_time)?.getTime() ?? 0;
    const tb = toJsDate(b.start_time)?.getTime() ?? 0;
    return ta - tb;
  });
  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  const targetEncounterId =
    first.encounter_id || mintedEncounterIdFor(first.id);

  // Adopt sequentially and fail loudly on the first denial so the user knows
  // their combine was incomplete (matches the legacy stamping behavior).
  for (const conv of ordered) {
    if (conv.id === first.id && !first.encounter_id) continue; // already home
    if (conv.encounter_id === targetEncounterId) continue; // idempotent
    try {
      await dlAdoptSessionIntoEncounter({
        encounterId: targetEncounterId,
        sessionId: conv.id,
      });
    } catch (err) {
      console.warn(
        `[encounterService] could not adopt ${conv.id} into ${targetEncounterId}; combine partially applied`,
        (err as any)?.message,
      );
      throw err;
    }
  }

  const resolvedPatientId = patientId ?? first.patient_id ?? null;
  const resolvedEhrPatientId = ehrPatientId ?? first.ehr_patient_id ?? null;
  const resolvedPatientName = patientName || first.patient_name || '';
  if (resolvedPatientId || resolvedEhrPatientId || resolvedPatientName) {
    await dlSetEncounterPatient({
      encounterId: targetEncounterId,
      patientId: resolvedPatientId,
      ehrPatientId: resolvedEhrPatientId,
      patientName: resolvedPatientName,
    }).catch(err => {
      console.warn(
        `[encounterService] combine: could not project patient onto ${targetEncounterId}`,
        (err as any)?.message,
      );
    });
  }

  const combinedSummary = last?.summary || '';
  if (combinedSummary) {
    await dlSetEncounterSummary({
      encounterId: targetEncounterId,
      summary: combinedSummary,
    }).catch(err => {
      console.warn(
        `[encounterService] combine: could not set summary on ${targetEncounterId}`,
        (err as any)?.message,
      );
    });
  }

  log.info('encounters_combined', {
    encounter_id: targetEncounterId,
    member_count: ordered.length,
  });

  // Legacy-shaped echo for callers that render the result immediately.
  const allClosed = ordered.every(c => Boolean(c.end_time));
  return {
    id: targetEncounterId,
    patient_id: resolvedPatientId,
    ehr_patient_id: resolvedEhrPatientId,
    patient_name: resolvedPatientName,
    company_id: companyId ?? first.company_id ?? null,
    contributor_user_ids: Array.from(
      new Set([creatorUserId, ...ordered.map(c => c.user_id).filter(Boolean)]),
    ),
    conversation_ids: ordered.map(c => c.id),
    status: allClosed ? ENCOUNTER_STATUS.CLOSED : ENCOUNTER_STATUS.OPEN,
    start_time: toJsDate(first.start_time) || null,
    end_time: allClosed ? toJsDate(last.end_time) || null : null,
    total_minutes: ordered.reduce(
      (sum, c) => sum + (Number(c.minutes) || 0),
      0,
    ),
    summary: combinedSummary,
    is_removed: false,
  };
}

/**
 * The single source of truth for any "list of sessions" surface that
 * shows a user's recent encounters. Used by:
 *   - Planner history and schedule (`usePlannerEncounterHistory`,
 *     `usePlannerSchedule`)
 *   - Scribe page sessions list (`ScribePage.SessionsListView`)
 *   - any future list surface (admin views, exports, etc.)
 *
 * Before this helper existed, each list surface loaded flat
 * `Conversation` rows straight from Firestore and rendered them one
 * row per Conversation. That meant a multi-conversation encounter
 * (resident chart prep + attending visit + a continuation recording)
 * showed up as 3 separate rows on every list view, even though the
 * patient detail card correctly grouped them as ONE encounter. The
 * user's report was "wet wipes encounter shows up twice in the
 * Encounters tab" — that was 4+ Conversation rows for one Encounter.
 *
 * Implementation: thin orchestration on top of pieces that already
 * exist. We deliberately don't re-fetch or re-shape anything; we just
 * compose the three existing primitives in one place so list surfaces
 * don't drift from each other:
 *
 *   1. The user's Conversations (paged + ordered exactly as the
 *      existing query returns them).
 *   2. The Encounter docs those Conversations reference (plus any
 *      patient-level encounter docs the resolver can find — but for
 *      the user-scoped list view we skip the patient-level lookup
 *      because we don't have a single patient context to query against).
 *   3. `groupConversationsIntoEncounters` to bucket each Conversation
 *      under its Encounter, build an EncounterView with the joint
 *      summary, total minutes, and contributor roster.
 *
 * Pagination caveat: pagination cursors are at the Conversation
 * layer, not the Encounter layer. If a single Encounter has members
 * that span two pages, the "first page" will return that encounter
 * once with the visible members and "next page" will return it again
 * with the next batch. Callers that need stable encounter pagination
 * should merge across pages by encounter id (the existing list UIs
 * are short enough that this isn't a problem in practice).
 *
 * `opts.workflowType` filters Conversations BEFORE grouping. Used by
 * the Scribe page (workflowType: 'medical_scribing') so the list
 * stays scribe-only without yanking live-translation encounters in via
 * cross-team siblings.
 */
export async function fetchUserEncounterList(
  userId: string | null | undefined,
  {
    startAfter = null,
    pageSize = 50,
    onlyRemoved = false,
    workflowType = null,
    companyId = null,
    startDate = null,
    endDate = null,
  }: Record<string, any> = {},
) {
  if (!userId) {
    return {encounters: [], conversations: [], lastDoc: null, hasMore: false};
  }

  // ── Schema V2 (only) path ──
  // The recents feed comes from GET /api/v1/sessions/recent (scope=mine) and
  // is mapped into the same EncounterView shape the list components render.
  // The trash view (`onlyRemoved`) comes from GET /api/v2/sessions/removed.
  // The old Firestore fallback query is gone — these lists are REST-only.
  //
  // Cursoring: the recents endpoint is limit-only (no cursor), so pagination
  // is emulated with an opaque `{__v2Offset}` lastDoc — each "load more"
  // re-fetches a larger window and returns the unseen tail. The endpoint
  // caps at 100 rows; beyond that hasMore goes false (recents are exactly
  // that — deep history stays reachable via the patient timeline).
  if (onlyRemoved) {
    const removed = await patientService.getRemovedSessions({
      limit: Math.min(pageSize, 100),
      startDate,
      endDate,
    });
    const conversations = workflowType
      ? removed.filter(
          (c: any) =>
            (c.workflow_type || c.workflowType || '') === workflowType,
        )
      : removed;
    return {
      encounters: groupConversationsIntoEncounters(conversations, new Map()),
      conversations,
      lastDoc: null,
      hasMore: false,
    };
  }

  const isV2Cursor =
    startAfter && typeof (startAfter as any).__v2Offset === 'number';
  const offset = isV2Cursor ? (startAfter as any).__v2Offset : 0;
  const requested = Math.min(offset + pageSize, 100);
  let rows = await fetchRecentSessions({
    scope: 'mine',
    limit: requested,
    startDate,
    endDate,
  });
  const rawCount = rows.length;
  if (workflowType) {
    rows = rows.filter(
      row => tagsToLegacyFields(row.tags || []).workflowType === workflowType,
    );
  }
  const pageRows = rows.slice(offset);
  const conversations = pageRows.map(recentSessionRowToLegacyConversation);
  const encounters = recentSessionsToEncounterViews(pageRows, {companyId});
  const hasMore = rawCount >= requested && requested < 100;
  return {
    encounters,
    conversations,
    lastDoc: hasMore ? {__v2Offset: rows.length} : null,
    hasMore,
  };
}

/**
 * Company-wide encounter browse.
 *
 * Schema V2: GET /api/v2/encounters — company scoping is resolved from the
 * bearer token, ordering is `updatedAt` desc with cursor pagination, and the
 * session-privacy visibility filter (private encounters hidden from
 * non-contributors) is enforced SERVER-side, so the `viewer`/`facilityId`
 * parameters are kept only for signature stability.
 *
 * Returns the same `{ encounters, lastDoc, hasMore }` shape as before;
 * `lastDoc` is an opaque `{__v2Cursor}` callers hand back for the next page.
 */
export async function fetchCompanyEncounterList(
  companyId: string | null | undefined,
  {
    startAfter: cursor = null,
    pageSize = 100,
    facilityId: _facilityId = null,
    viewer: _viewer = null,
  }: Record<string, any> = {},
) {
  if (!companyId) {
    return {encounters: [], lastDoc: null, hasMore: false};
  }
  const {encounters: rows, nextCursor} = await fetchCompanyEncounters({
    limit: Math.min(pageSize, 100),
    cursor:
      cursor && typeof (cursor as any).__v2Cursor === 'string'
        ? (cursor as any).__v2Cursor
        : null,
  });
  return {
    encounters: rows.map(row =>
      companyEncounterRowToLegacyEncounterDoc(row, {companyId}),
    ),
    lastDoc: nextCursor ? {__v2Cursor: nextCursor} : null,
    hasMore: Boolean(nextCursor),
  };
}

/**
 * Company-wide encounter text search by patient name + summary.
 *
 * Schema V2: GET /api/v2/encounters?q=. IMPORTANT SEMANTICS: `q` is a
 * WINDOWED substring filter — the server applies a naive case-insensitive
 * substring match over patient_name/summary to the fetched page window
 * (ordered `updatedAt` desc), NOT a full-history text index. This is parity
 * with the client-side filtering the browse surfaces did before; matches
 * beyond the current window are reached by paging (`lastDoc` cursor), and an
 * empty page with `hasMore: true` means "keep paging", not "no results".
 *
 * Same `{ encounters, lastDoc, hasMore }` shape as fetchCompanyEncounterList.
 * An empty query yields no results (callers branch to the browse list).
 */
export async function searchCompanyEncountersByText(
  companyId: string | null | undefined,
  searchText: unknown,
  {
    startAfter: cursor = null,
    pageSize = 50,
    viewer: _viewer = null,
  }: Record<string, any> = {},
) {
  const q = String(searchText ?? '').trim();
  if (!companyId || !q) {
    return {encounters: [], lastDoc: null, hasMore: false};
  }
  const {encounters: rows, nextCursor} = await fetchCompanyEncounters({
    limit: Math.min(pageSize, 100),
    cursor:
      cursor && typeof (cursor as any).__v2Cursor === 'string'
        ? (cursor as any).__v2Cursor
        : null,
    q,
  });
  return {
    encounters: rows.map(row =>
      companyEncounterRowToLegacyEncounterDoc(row, {companyId}),
    ),
    lastDoc: nextCursor ? {__v2Cursor: nextCursor} : null,
    hasMore: Boolean(nextCursor),
  };
}

/**
 * Regenerate the encounter-level summary from the full interleaved
 * transcript across every sibling Conversation the caller can read,
 * and write it to `Encounter.summary`. Fires from
 * `ScribePage.handleEndSession` after a scribe recording lands inside
 * a multi-conversation encounter, so the summary visible on the
 * patient detail card reflects the *combined* visit — not the last
 * individual contribution.
 *
 * Why client-side and not on read:
 *   - Multiple viewers loading the same patient would otherwise fire N
 *     redundant LLM calls and race each other on the doc write.
 *   - The trigger is naturally rate-limited by "someone ended a new
 *     recording in this encounter" — exactly when the joint summary
 *     could have meaningfully changed.
 *   - Cross-company contributors can't update the Encounter doc (the
 *     rule requires `company_id == userCompanyId()` on update). We
 *     swallow that denial — a same-company user will refresh the
 *     summary the next time they end a session, which is the durable
 *     fix.
 *
 * Best-effort and non-fatal: the per-Conversation summary write that
 * preceded this still landed, so even if the encounter summary
 * regeneration fails, the encounter card's fallback to
 * `last_member_conversation.summary` keeps the UI from going blank.
 *
 * Parameters:
 *   encounterId  — real Encounter doc id; implicit ids and null are
 *                  no-ops.
 *   opts.generateSummary — pluggable summarizer for testability. The
 *                  production caller passes
 *                  `scribeService.generateConversationSummary`, which
 *                  this module deliberately doesn't import to avoid
 *                  pulling the entire scribe-prompt module graph into
 *                  every Encounter helper consumer.
 */
export async function regenerateEncounterSummary(
  encounterId: string | null | undefined,
  {generateSummary}: {generateSummary?: (...args: any[]) => any} = {},
) {
  if (!encounterId || isImplicitEncounterId(encounterId)) return;
  if (typeof generateSummary !== 'function') {
    console.warn(
      '[encounterService] regenerateEncounterSummary called without a generateSummary fn; skipping',
    );
    return;
  }

  // 1) Load the encounter and every visible sibling Conversation +
  //    messages, in encounter-aware aggregated form.
  //
  // We reuse `loadEncounterAwareMessages` rather than re-implementing
  // the fan-out so the summary is computed from the exact same
  // interleaved-with-segment-markers transcript that note generation
  // would see. That keeps the summary and the eventual generated note
  // semantically aligned (the LLM scoring "what was this visit
  // about?" against the same input the HPI prompt got).
  //
  // We pick any one sibling as the `conversationId` seed — typically
  // the caller's just-ended Conversation — but loadEncounterAwareMessages
  // ultimately drives off the encounter's member list, so the seed only
  // needs to belong to the encounter.
  let enc;
  try {
    enc = await getEncounter(encounterId);
  } catch (err) {
    console.warn(
      `[encounterService] regenerateEncounterSummary: could not read Encounter/${encounterId}`,
      err?.message,
    );
    return;
  }
  if (!enc) return;
  const memberIds = Array.isArray(enc.conversation_ids)
    ? enc.conversation_ids
    : [];
  if (memberIds.length === 0) return;

  const seedId = memberIds[0];
  let aggregated;
  try {
    aggregated = await loadEncounterAwareMessages({conversationId: seedId});
  } catch (err) {
    console.warn(
      `[encounterService] regenerateEncounterSummary: aggregation failed for encounter ${encounterId}`,
      err?.message,
    );
    return;
  }

  const aggregatedMessages = aggregated?.messages || [];
  if (aggregatedMessages.length === 0) {
    // Nothing readable for this caller (or genuinely empty). Don't
    // overwrite a previously good summary with an empty value.
    return;
  }

  // 2) Run the summarizer on the aggregated transcript.
  let summary;
  try {
    summary = await generateSummary(aggregatedMessages);
  } catch (err) {
    console.warn(
      `[encounterService] regenerateEncounterSummary: summarizer threw for encounter ${encounterId}`,
      err?.message,
    );
    return;
  }
  if (!summary || summary === 'Healthcare Conversation') {
    // Empty means the summarizer could not produce a real summary.
    // The legacy placeholder check keeps older callers/tests safe.
    return;
  }

  // 3) Write through PATCH /api/v2/encounters/:id/summary — the server
  //    projects it onto the encounter (V2 + legacy, including the search
  //    index) and mirrors it onto each member session's legacy Conversation
  //    doc. Access denials (e.g. non-contributor) log and continue rather
  //    than surfacing as user-visible failure.
  try {
    await dlSetEncounterSummary({encounterId, summary});
  } catch (err) {
    console.warn(
      `[encounterService] regenerateEncounterSummary: write denied for encounter ${encounterId}`,
      err?.message,
    );
  }
}

/**
 * Ensure a patient-linked Conversation's encounter carries the patient
 * identity, and hand back that encounter id.
 *
 * Schema V2 rewrite: the ENCOUNTER ALWAYS EXISTS server-side — every new
 * session was started through dlStartSession (which created or joined one),
 * and every legacy/agent-created session gets a minted `enc-{sessionId}`
 * twin from the sync. So the old create-and-back-link dance is gone; what
 * remains is:
 *   1. Resolve the session's encounter id — caller hint → the session's own
 *      binding (REST/cached) → the minted twin.
 *   2. Project the patient onto it via POST /api/v2/encounters/:id/patient,
 *      so the visit shows up on the patient timeline
 *      (GET /api/v1/patients/:id/encounters) for the whole care team.
 *
 * Contract preserved from the legacy version:
 *   - No-op (returns null) when there's no patient or no creator/company —
 *     encounters are patient-scoped on the timeline.
 *   - Returns the encounter id on success, null on any non-fatal failure
 *     (session not yet synced to V2, access denial), with a warning logged.
 *     Callers chain `regenerateEncounterSummary` on the returned id.
 *
 * The extra legacy parameters (workflow/template/times/minutes/telephonic/
 * visibility) are accepted for signature stability; those properties are
 * derived server-side from the member sessions now.
 */
export async function ensureEncounterForConversation({
  conversationId,
  patientId = null,
  ehrPatientId = null,
  patientName = '',
  companyId,
  facilityId: _facilityId = '',
  creatorUserId,
  workflowType = 'medical_scribing',
  templateType: _templateType = 'soap',
  specialty: _specialty = '',
  startTime: _startTime = null,
  endTime: _endTime = null,
  minutes: _minutes = 0,
  isTelephonic = false,
  restrictedVisibility: _restrictedVisibility = false,
}: Record<string, any>) {
  if (!conversationId) return null;
  if (!creatorUserId || !companyId) return null;
  if (!patientId && !ehrPatientId) return null;

  try {
    const encounterId =
      await patientService.resolveEncounterIdForSession(conversationId);
    await dlSetEncounterPatient({
      encounterId,
      patientId: patientId || null,
      ehrPatientId: ehrPatientId || null,
      patientName: patientName || '',
    });
    log.info('ensure_encounter_created', {
      conversation_id: conversationId,
      encounter_id: encounterId,
      company_id: companyId,
      workflow_type: workflowType,
      is_telephonic: Boolean(isTelephonic),
    });
    return encounterId;
  } catch (err) {
    console.warn(
      '[encounterService] ensureEncounter: patient projection failed',
      (err as any)?.message,
    );
    log.error('ensure_encounter_create_failed', {
      conversation_id: conversationId,
      company_id: companyId,
      has_patient_id: Boolean(patientId),
      has_ehr_patient_id: Boolean(ehrPatientId),
      ...describeError(err),
    });
    return null;
  }
}

/**
 * DEPRECATED no-op (kept only for signature stability at old call sites).
 *
 * The encounter's open/closed status is a SERVER-owned projection now:
 * every session end that goes through the data layer (dlEndSession, or the
 * legacy→V2 sync for agent-ended sessions) recomputes the encounter
 * aggregates — including the legacy `status: 'closed'` projection this
 * helper used to maintain from the client. A client-side status write would
 * only race that projection.
 */
export async function endScribeEncounterIfFullyClosed(
  _encounterId: string | null | undefined,
) {
  /* server-owned projection — nothing to do client-side */
}

/**
 * Mark an Encounter "done." Used by the Mark done button on the patient
 * detail card when a provider wants to explicitly close out an encounter
 * — typically because a member session never received a proper end
 * (browser crash, tab close mid-recording, etc.) and the open status would
 * otherwise keep blocking the Continue affordance.
 *
 * Schema V2: POST /api/v2/encounters/:id/close — the server force-closes
 * every member session still created/active and flips the encounter's
 * status projection in one transition. Billing is deliberately NOT touched
 * (the crashed recording lost its minutes; inventing them would be worse).
 * `reason` lands on the legacy `closed_reason` stamp.
 *
 * Idempotent: closing an already-closed encounter is a no-op server-side; a
 * 404 (encounter never materialized) is swallowed for parity with the old
 * "missing doc → return" behavior.
 */
export async function closeEncounter(
  encounterId: string,
  {reason = 'manual_mark_done'}: {reason?: string} = {},
) {
  if (!encounterId || isImplicitEncounterId(encounterId)) return;
  try {
    await dlCloseEncounter({encounterId, reason});
  } catch (err: any) {
    const status = err?.status ?? err?.statusCode;
    if (status === 404) return; // missing doc — legacy no-op parity
    throw err;
  }
  log.info('encounter_closed', {
    encounter_id: encounterId,
    reason,
  });
}

/**
 * Fetch all Encounters for a patient, scoped to a company.
 *
 * Each (patientId, ehrPatientId) target is queried independently and merged.
 * If the composite index for `(company_id, patient_id, start_time desc)` (or
 * its ehr variant) isn't built yet on a fresh project, we retry with the
 * `where` clauses only — `orderBy`/`limit` are recomputed in JS so the UI
 * keeps rendering instead of going dark while indexes catch up.
 */
export async function fetchEncountersForPatient({
  patientId = null,
  ehrPatientId = null,
  companyId,
  pageSize = 25,
  // {userId, role} of the requesting provider — the patient timeline shows
  // colleagues' encounters, so private ones must be dropped here for
  // non-contributors. No viewer = fail closed on private encounters.
  viewer = null,
  patientName = '',
}: Record<string, any> = {}) {
  if (!companyId) return [];

  // ── Schema V2 path ──
  // Patient history comes from GET /api/v1/patients/:id/encounters (V2
  // `Encounter.patientId` index; tenancy, soft-deletes and session-privacy
  // enforced server-side, so no client-side viewer filter is needed).
  //
  // The EHR-ONLY arm goes through GET /api/v2/encounters?ehrPatientId= —
  // an exact match on the legacy `ehr_patient_id` mirror, WINDOWED to the
  // company's newest ~200 encounters (no composite index server-side). An
  // EHR patient whose last visit is older than that window returns empty;
  // the timeline merge tolerates the miss. BH visibility is enforced
  // server-side on both arms, so the old client-side `viewer` filter is
  // gone (the parameter stays for signature stability).
  let v2Rows: any[] = [];
  if (patientId) {
    try {
      const rows = await fetchPatientEncounters(patientId, {
        limit: Math.min(pageSize, 50),
      });
      v2Rows = rows.map(row =>
        patientEncounterRowToLegacyEncounterDoc(row, {
          patientId,
          ehrPatientId,
          patientName,
          companyId,
        }),
      );
    } catch (err) {
      console.warn(
        '[encounterService] V2 patient encounters read failed',
        err?.message,
      );
    }
  }
  if (!ehrPatientId) {
    return v2Rows
      .sort((a, b) => {
        const ta = toJsDate(a.start_time)?.getTime() ?? 0;
        const tb = toJsDate(b.start_time)?.getTime() ?? 0;
        return tb - ta;
      })
      .slice(0, pageSize);
  }

  let ehrRows: any[] = [];
  try {
    const {encounters} = await fetchCompanyEncounters({
      ehrPatientId,
      limit: Math.min(pageSize, 100),
    });
    ehrRows = encounters.map(row =>
      companyEncounterRowToLegacyEncounterDoc(row, {companyId}),
    );
  } catch (err) {
    console.warn(
      '[encounterService] V2 EHR-patient encounters read failed',
      (err as any)?.message,
    );
  }

  // Merge the two arms (dedup by encounter id); the patient-history rows win
  // on collisions since they're the patient-scoped projection source.
  const mergedById = new Map<string, any>();
  for (const row of ehrRows) mergedById.set(row.id, row);
  for (const row of v2Rows) mergedById.set(row.id, row);
  return Array.from(mergedById.values())
    .sort((a, b) => {
      const ta = toJsDate(a.start_time)?.getTime() ?? 0;
      const tb = toJsDate(b.start_time)?.getTime() ?? 0;
      return tb - ta;
    })
    .slice(0, pageSize);
}

/**
 * Load the message set that should be fed into note generation for the given
 * primary Conversation:
 *
 *   - If the conversation has no `encounter_id`, returns its own messages
 *     unchanged (full backwards compatibility — existing single-session
 *     callers see identical behavior).
 *   - If the conversation has an `encounter_id`, fetches every member
 *     conversation in the encounter and aggregates their messages with
 *     labeled segment markers via `aggregateEncounterMessages`.
 *
 * Returns `{ messages, segments, sourceConversations, encounter }`. The
 * `segments` and `encounter` fields are null/empty for the single-session
 * (legacy) path so callers can detect "this is a single conversation" with
 * a cheap truthy check.
 *
 * Failure modes are non-fatal: if the encounter or a sibling conversation
 * can't be loaded (typically a Firestore rules denial because the current
 * user lacks facility access to a peer's session), we fall back to the
 * primary conversation's messages alone and surface the issue in
 * `partial: true`. The note still generates from what we *can* read.
 */
export async function loadEncounterAwareMessages({
  conversationId,
  conversation = null,
  primaryMessages = null,
}: Record<string, any> = {}) {
  if (!conversationId && !conversation?.id) {
    return {
      messages: [],
      segments: [],
      sourceConversations: [],
      encounter: null,
      partial: false,
    };
  }
  // No Firestore fallback for the primary doc anymore: callers that have the
  // conversation object pass it; otherwise the REST events read below (via
  // getSessionMessages) also resolves the session's encounter binding.
  const primary = conversation || null;

  // Use caller-provided messages when available — saves a refetch on the
  // PostSessionNotesView mount path where we already loaded them.
  const primaryId = primary?.id || conversationId;
  const ownMessages =
    primaryMessages ||
    (await patientService.getSessionMessages(primaryId).catch(() => []));

  // Encounter binding: the conversation object's own `encounter_id` wins;
  // otherwise use the binding learned from the REST session read (cached by
  // getSessionMessages — covers the caller-provided-messages path too since
  // those flowed through the same read earlier in the session).
  const encounterId =
    primary?.encounter_id ||
    (await patientService
      .resolveEncounterIdForSession(primaryId)
      .catch(() => null));
  if (!encounterId || isImplicitEncounterId(encounterId)) {
    return {
      messages: ownMessages,
      segments: [],
      sourceConversations: primary ? [primary] : [],
      encounter: null,
      partial: false,
    };
  }

  // Multi-conversation encounter — load siblings + their messages.
  let encounter: any = null;
  let siblings: any[] = [];
  let partial = false;
  try {
    encounter = await getEncounter(encounterId);
  } catch (err) {
    console.warn(
      `[encounterService] could not load Encounter/${encounterId}`,
      err.message,
    );
    partial = true;
  }
  try {
    // The REST encounter read already carries the member-session summaries —
    // reuse them instead of a second detail fetch.
    siblings = encounter?._v2Sessions
      ? encounter._v2Sessions
          .map((session: any) =>
            encounterSessionSummaryToLegacyConversation(session, {
              encounterId: encounter.id,
              patientId: encounter.patient_id ?? null,
              patientName: encounter.patient_name || '',
              summary: encounter.summary || '',
            }),
          )
          .sort(
            (a: any, b: any) =>
              (a.start_time?.getTime?.() ?? 0) -
              (b.start_time?.getTime?.() ?? 0),
          )
      : await patientService.fetchConversationsForEncounter(encounterId);
  } catch (err) {
    console.warn(
      `[encounterService] could not load sibling conversations for ${encounterId}`,
      err.message,
    );
    partial = true;
    siblings = primary ? [primary] : [];
  }

  // Ensure the primary conversation is in the sibling list (it may not be if
  // an indexing race has the encounter doc but not the encounter_id back-ref
  // visible yet).
  if (primary && !siblings.some(s => s.id === primary.id)) {
    siblings = [...siblings, primary];
  }

  // ── Patient-safety guard (the SHC "two patients in one note" fix) ──────────
  // An encounter is only ever supposed to group conversations for the SAME
  // patient. But a still-open prior session can leave an encounter "open", and
  // a continue/back-link race could bind a *new patient's* session into it.
  // If that ever happens we must NOT fold both patients' transcripts into one
  // note. Drop any sibling that is provably a different patient, or that is
  // still un-ended (partial / abandoned recording), keeping the primary
  // conversation itself untouched.
  const {kept, dropped} = filterAggregatableSiblings(primary, siblings);
  if (dropped.length > 0) {
    partial = true;
    const primaryIdentity = getPatientIdentity(primary);
    console.warn(
      `[encounterService] loadEncounterAwareMessages: excluded ${dropped.length} sibling(s) from encounter ${encounterId} note aggregation`,
      dropped,
    );
    captureGuardEvent('encounter_aggregation_sibling_dropped', {
      encounter_id: encounterId,
      primary_conversation_id: primaryId,
      primary_patient_id: primaryIdentity.patientId || null,
      dropped_count: dropped.length,
      cross_patient_count: dropped.filter(d => d.reason === 'cross_patient')
        .length,
      unended_count: dropped.filter(d => d.reason === 'unended').length,
      dropped: dropped.slice(0, 20),
    });
    log.warn('aggregation_sibling_dropped', {
      encounter_id: encounterId,
      primary_conversation_id: primaryId,
      primary_patient_id: primaryIdentity.patientId || null,
      dropped_count: dropped.length,
      cross_patient_count: dropped.filter(d => d.reason === 'cross_patient')
        .length,
      unended_count: dropped.filter(d => d.reason === 'unended').length,
    });
  }
  siblings = kept;

  // Single-member encounter: don't bother aggregating — produce a clean
  // single-conversation result so the no-marker path is preserved.
  if (siblings.length <= 1) {
    return {
      messages: ownMessages,
      segments: [],
      sourceConversations: siblings.length
        ? siblings
        : primary
          ? [primary]
          : [],
      encounter,
      partial,
    };
  }

  const messagesByConversationId: Record<string, any[]> = {};
  // The primary conversation's messages we already have; for siblings we
  // fetch in parallel. Any sibling we can't read (Firestore rules denial)
  // gets an empty array; we still keep the segment marker so the LLM knows
  // a contributor existed even if we couldn't see what they said.
  messagesByConversationId[primaryId] = ownMessages;
  const siblingFetches = siblings
    .filter(s => s.id !== primaryId)
    .map(async s => {
      try {
        const msgs = await patientService.getSessionMessages(s.id);
        messagesByConversationId[s.id] = msgs;
      } catch (err) {
        console.warn(
          `[encounterService] could not load Messages for ${s.id}`,
          err.message,
        );
        messagesByConversationId[s.id] = [];
        partial = true;
      }
    });
  await Promise.all(siblingFetches);

  const {messages, segments} = aggregateEncounterMessages({
    conversations: siblings,
    messagesByConversationId,
  });
  return {
    messages,
    segments,
    sourceConversations: siblings,
    encounter,
    partial,
  };
}

/**
 * Load sibling Conversations (member sessions) for one or more Encounters,
 * in the legacy Conversation view-model shape.
 *
 * `encounters` can be either an array of encounter IDs (legacy callers) or
 * an array of encounter doc objects. Docs that came from the V2 REST reads
 * carry their member-session summaries inline (`_v2Sessions`) and are mapped
 * locally with zero extra requests; ids / legacy docs fall back to a
 * per-encounter GET /api/v1/encounters/:id detail read. Failure on any
 * single encounter is non-fatal — it contributes an empty array and the
 * rest still resolve.
 */
export async function fetchSiblingConversationsForEncounters(
  encounters: any[] = [],
) {
  const items = (encounters || []).filter(Boolean);
  if (items.length === 0) return [];
  const arrays = await Promise.all(
    items.map(item => {
      const encounterId = typeof item === 'string' ? item : item.id;
      if (!encounterId || isImplicitEncounterId(encounterId))
        return Promise.resolve([]);
      if (typeof item !== 'string' && Array.isArray(item._v2Sessions)) {
        return Promise.resolve(
          item._v2Sessions.map((session: any) =>
            encounterSessionSummaryToLegacyConversation(session, {
              encounterId,
              patientId: item.patient_id ?? null,
              ehrPatientId: item.ehr_patient_id ?? null,
              patientName: item.patient_name || '',
              summary: item.summary || '',
              companyId: item.company_id ?? null,
            }),
          ),
        );
      }
      return patientService
        .fetchConversationsForEncounter(encounterId)
        .catch(err => {
          console.warn(
            `[encounterService] sibling fetch for encounter ${encounterId} failed`,
            err?.message,
          );
          return [];
        });
    }),
  );
  const merged = [];
  const seen = new Set();
  for (const arr of arrays) {
    for (const conv of arr) {
      if (conv?.id && !seen.has(conv.id)) {
        seen.add(conv.id);
        merged.push(conv);
      }
    }
  }
  return merged;
}

/**
 * Load every real Encounter referenced by the supplied Conversations, plus
 * any Encounters for this patient that the caller may not have member
 * conversations for. Returns a Map keyed by encounter id, suitable to hand
 * to `groupConversationsIntoEncounters`.
 */
export async function fetchEncounterMapForGrouping({
  conversations = [],
  patientId = null,
  ehrPatientId = null,
  companyId = null,
  // Threaded to the patient-level lookup only. The per-doc lookups below are
  // keyed off Conversations the caller already legitimately holds (their own
  // sessions or fetched siblings), so they stay unfiltered — otherwise a
  // caller that omits `viewer` would hide a private encounter from its own
  // creator's list views.
  viewer = null,
}: Record<string, any> = {}) {
  const conversationEncounterIds = Array.from(
    new Set(conversations.map((c: any) => c.encounter_id).filter(Boolean)),
  );
  const lookups = [];
  if (conversationEncounterIds.length > 0) {
    lookups.push(
      (async () => {
        const out = new Map();
        // Per-id REST detail reads (GET /api/v1/encounters/:id) in parallel —
        // the patient detail loads infrequently and the lists are short.
        await Promise.all(
          conversationEncounterIds.map(async (id: any) => {
            try {
              const encDoc = await getEncounter(id);
              if (encDoc) out.set(id, encDoc);
            } catch (err) {
              console.warn(
                `[encounterService] could not load Encounter/${id}`,
                err.message,
              );
            }
          }),
        );
        return out;
      })(),
    );
  }
  if (companyId && (patientId || ehrPatientId)) {
    lookups.push(
      fetchEncountersForPatient({
        patientId,
        ehrPatientId,
        companyId,
        viewer,
      }).then(arr => new Map(arr.map(e => [e.id, e]))),
    );
  }
  const maps = await Promise.all(lookups);
  const merged = new Map();
  for (const m of maps) for (const [k, v] of m) merged.set(k, v);
  return merged;
}
